import { FormArray, AbstractControl } from '@angular/forms';
import { ActionIcons, ModeIcons } from '../utility/constants';

/**
 * True when a charge grid row should participate in save validation
 * (editable input row with data, or any non-disabled data row).
 */
export function chargeGridHasActionableValidationErrors(
  rows: FormArray,
  pageMode: string,
  inputRowHasData: (rowValue: Record<string, unknown>) => boolean
): boolean {
  for (let index = 0; index < rows.length; index++) {
    const control = rows.at(index);
    if (!shouldValidateChargeRow(control, index, pageMode, inputRowHasData)) {
      continue;
    }
    if (control.invalid) {
      return true;
    }
  }
  return false;
}

/** Restore disabled vs editable state when re-loading the grid from the in-memory collection. */
export function resolveChargeRowDisabledOnPatch(
  pageMode: string,
  dataIndex: number,
  isRowDisable: boolean | undefined
): boolean {
  if (pageMode === ModeIcons.view) {
    return true;
  }
  if (dataIndex === 0) {
    return false;
  }
  if (isRowDisable === false) {
    return false;
  }
  return true;
}

export function apChargeDraftRowHasData(
  row: Record<string, unknown>
): boolean {
  return !!(
    row['invoiceNumber'] ||
    row['agentChargeCode'] ||
    row['chargeAmount'] ||
    row['incomingChargeAmount']
  );
}

export function arChargeDraftRowHasData(row: Record<string, unknown>): boolean {
  return !!(
    row['invoiceNumber'] || row['chargeCode'] || row['chargeAmount']
  );
}

/** Pending editable AP rows in the in-memory collection (tab closed) with missing required values. */
export function apChargeStoreHasIncompletePendingRows(
  rows: { isRowDisable?: boolean }[]
): boolean {
  for (const row of rows) {
    if (row.isRowDisable !== false) {
      continue;
    }
    const record = row as Record<string, unknown>;
    if (!apChargeDraftRowHasData(record)) {
      continue;
    }
    const amount = record['incomingChargeAmount'] ?? record['chargeAmount'];
    if (
      !record['invoiceNumber'] ||
      amount === '' ||
      amount === null ||
      amount === undefined
    ) {
      return true;
    }
  }
  return false;
}

export function arChargeStoreHasIncompletePendingRows(
  rows: { isRowDisable?: boolean }[]
): boolean {
  for (const row of rows) {
    if (row.isRowDisable !== false) {
      continue;
    }
    const record = row as Record<string, unknown>;
    if (!arChargeDraftRowHasData(record)) {
      continue;
    }
    if (
      AR_SHIPMENT_TAB_REQUIRED_KEYS.some(
        key => !hasChargeFieldValue(record[key])
      )
    ) {
      return true;
    }
  }
  return false;
}

/** Editable (draft or edited) rows: blank rows are ignored, rows with any value are validated. */
function shouldValidateChargeRow(
  control: AbstractControl,
  index: number,
  pageMode: string,
  inputRowHasData: (rowValue: Record<string, unknown>) => boolean
): boolean {
  const isInputRow = index === 0 && pageMode !== ModeIcons.view;
  const isEditableRow =
    isInputRow || control.get('isRowDisable')?.value === false;
  if (!isEditableRow) {
    return false;
  }
  return inputRowHasData(control.getRawValue() as Record<string, unknown>);
}

/** Row is still being entered or edited (not committed / read-only). */
export function isEditableChargeRow(control: AbstractControl): boolean {
  return control.enabled;
}

/** Indexes of editable rows that carry at least one value. */
export function getEditableChargeRowIndexesWithData(
  rows: FormArray,
  rowHasData: (rowValue: Record<string, unknown>) => boolean
): number[] {
  const indexes: number[] = [];
  rows.controls.forEach((control, index) => {
    if (
      isEditableChargeRow(control) &&
      rowHasData(control.getRawValue() as Record<string, unknown>)
    ) {
      indexes.push(index);
    }
  });
  return indexes;
}

/**
 * True when any listed row fails validators or misses a mandatory value.
 * Empty mandatory fields get a `required` error so the grid shows its inline error icon;
 * the error clears on the next value change of that field.
 */
export function markInvalidChargeRows(
  rows: FormArray,
  rowIndexes: number[],
  getRequiredKeys: (rowValue: Record<string, unknown>) => string[]
): boolean {
  let hasErrors = false;
  rowIndexes.forEach(index => {
    const control = rows.at(index);
    const rowValue = control.getRawValue() as Record<string, unknown>;
    getRequiredKeys(rowValue).forEach(key => {
      const field = control.get(key);
      if (field && !hasChargeFieldValue(rowValue[key])) {
        field.setErrors({ ...(field.errors ?? {}), required: true });
        field.markAsTouched();
      }
    });
    if (control.invalid || hasMissingRequiredValue(rowValue, getRequiredKeys)) {
      hasErrors = true;
      control.markAllAsTouched();
    }
  });
  return hasErrors;
}

function hasMissingRequiredValue(
  rowValue: Record<string, unknown>,
  getRequiredKeys: (rowValue: Record<string, unknown>) => string[]
): boolean {
  return getRequiredKeys(rowValue).some(
    key => !hasChargeFieldValue(rowValue[key])
  );
}

export const AR_SHIPMENT_TAB_REQUIRED_KEYS = ['chargeCode', 'chargeAmount'];

export function hasChargeFieldValue(value: unknown): boolean {
  return value !== null && value !== undefined && String(value).trim() !== '';
}

/** Charge grid header icons: grid Save is only offered in page view mode (direct API save). */
export function withGridSaveForViewModeOnly<T extends { name: string }>(
  icons: T[],
  isPageViewMode: boolean
): T[] {
  return isPageViewMode
    ? icons
    : icons.filter(icon => icon.name !== ActionIcons.save);
}
